import React, { useCallback, useEffect, useState } from 'react';

import { Flex, Radio, Space, Tag } from 'antd';

import useSearchPartner from '../hooks/useSearchPartner';
import SearchObject from '@/components/SearchObject';
import useError from '@/hooks/useError';
interface ISearchPartner {
  disabled?: boolean;
  partnerId?: string;
  searchMultiple?: boolean;
  setPartnerId: (id: string) => void;
  selectedPartner?: string;
  setSelectedPartner?: (partner: string | undefined) => void;
  setServiceLevel?: (id: string) => void;
}

const SearchPartner = ({
  disabled = false,
  searchMultiple = false,
  partnerId,
  setPartnerId,
  selectedPartner,
  setSelectedPartner,
  setServiceLevel,
}: ISearchPartner) => {
  const addError = useError();
  const { getSinglePartner, getMultiplePartner, getPartnerById } =
    useSearchPartner({
      showError: false,
    });
  const [searchField, setSearchField] = useState<string>('');
  const [searchResults, setSearchResults] = useState<Array<any>>([]);

  const handleFindName = useCallback(
    (searchName: string) => {
      if (searchName) {
        getSinglePartner(searchName).then((res) => {
          if (!res.success) {
            res.size === 0
              ? addError('No partner found')
              : addError('Multiple partners found');
          } else {
            setPartnerId(res.partner.id);
            setSearchField(res.partner.name);
          }
        });
      }
    },
    [addError, getSinglePartner, setPartnerId]
  );

  const handleFindMultipleByName = useCallback(
    (searchName: string) => {
      if (searchName) {
        getMultiplePartner(searchName).then((res) => {
          if (!res.success) {
            res.size === 0
              ? addError('No partner found')
              : addError('An Error has occured, please retry later');
          } else {
            setSearchResults(res.partners);
          }
        });
      }
    },
    [addError, getMultiplePartner]
  );

  const handlePartnerChoice = useCallback(
    (e) => {
      setPartnerId(e.target.value);
      const selectedpartner = searchResults.find(
        (res) => res.id === e.target.value
      );
      const parnterName = selectedpartner.name;
      const partnerServiceLvl = selectedpartner.serviceLevel.id.toString();
      setSearchField(parnterName);

      if (setSelectedPartner) {
        setSelectedPartner(parnterName);
      }
      if (setServiceLevel) {
        setServiceLevel(partnerServiceLvl);
      }
    },
    [
      setPartnerId,
      setSearchField,
      searchResults,
      setSelectedPartner,
      setServiceLevel,
    ]
  );

  useEffect(() => {
    setSearchField(selectedPartner ? selectedPartner : '');
  }, [setSearchField, selectedPartner]);

  // Load partner name from API if partnerId is provided but selectedPartner is not
  useEffect(() => {
    if (partnerId && !selectedPartner && !searchField) {
      getPartnerById(partnerId).then((res) => {
        if (res.success && res.partner) {
          setSearchField(res.partner.name);
          if (setSelectedPartner) {
            setSelectedPartner(res.partner.name);
          }
        } else {
          // If partner not found, show the ID at least
          setSearchField(`Partner ID: ${partnerId}`);
        }
      });
    }
  }, [
    partnerId,
    selectedPartner,
    searchField,
    getPartnerById,
    setSelectedPartner,
    setSearchField,
  ]);

  const displayName = (partner: any) => {
    const name = (
      <Flex gap="0.5rem" wrap="wrap">
        <strong>{partner.name}</strong>{' '}
        {partner.clientId.length > 0 && (
          <Tag color="green" bordered={false}>
            {partner.clientId}
          </Tag>
        )}
        <Tag color="blue" bordered={false}>
          {partner.id}
        </Tag>
        {partner.archived && (
          <Tag color="volcano" bordered={false}>
            Archived
          </Tag>
        )}
      </Flex>
    );

    return name;
  };

  // Wrapper to handle reset and notify parent
  const handleSetPartnerId = useCallback(
    (id: string) => {
      setPartnerId(id);
      // When id is cleared (reset), also clear the selected partner in parent
      if (!id && setSelectedPartner) {
        setSelectedPartner(undefined);
      }
    },
    [setPartnerId, setSelectedPartner]
  );

  return (
    <>
      <SearchObject
        searchField={searchField}
        setSearchField={setSearchField}
        objectId={partnerId}
        setObjectId={handleSetPartnerId}
        disabled={disabled}
        placeholder="Name..."
        searchLabel="Partner (search by name) - First 15 results displayed"
        foundLabel="Partner"
        setSearchResults={setSearchResults}
        handleFind={searchMultiple ? handleFindMultipleByName : handleFindName}
      />
      {searchResults.length > 0 && (
        <Radio.Group onChange={(e) => handlePartnerChoice(e)}>
          <Space direction="vertical">
            {searchResults.map((partner) => (
              <Radio
                value={partner.id}
                key={partner.id}
                style={{
                  marginTop: '10px',
                }}
              >
                {displayName(partner)}
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      )}
    </>
  );
};

export default SearchPartner;
